"use client";

import { useState, useEffect } from "react";
import Cookies from "universal-cookie";
import { X, Shield } from "lucide-react";
import { Button } from "@heroui/button";
import { Card, CardHeader, CardBody, CardFooter } from "@heroui/card";

const cookies = new Cookies();

export default function ConsentBanner() {
  const [accepted, setAccepted] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [cookieSettings, setCookieSettings] = useState({
    essential: true, // Always enabled
    analytics: true, // Always granted
    advertising: false, // Optional
  });

  useEffect(() => {
    const consentValue = cookies.get("consent");
    if (consentValue === undefined) {
      setAccepted(false);
      setIsVisible(true);
      // Set initial GTM consent with analytics granted
      updateConsent({ analytics: true, advertising: false });
    } else {
      const savedSettings = cookies.get("consent_settings");
      if (savedSettings) {
        setCookieSettings(savedSettings);
        updateConsent(savedSettings);
      }
    }
  }, []);

  const updateConsent = (settings) => {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("consent", "update", {
        ad_storage: settings.advertising ? "granted" : "denied",
        analytics_storage: "granted", // Always granted
      });
    } else {
      console.warn("GTM not loaded yet; consent update skipped.");
    }
  };

  const handleAcceptAll = () => {
    const newSettings = { essential: true, analytics: true, advertising: true };
    setCookieSettings(newSettings);
    updateConsent(newSettings);
    cookies.set("consent", true, {
      path: "/",
      expires: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000),
    });
    cookies.set("consent_settings", newSettings, {
      path: "/",
      expires: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000),
    });
    setIsVisible(false);
    setTimeout(() => setAccepted(true), 300);
  };

  const handleAcceptEssential = () => {
    const newSettings = {
      essential: true,
      analytics: true,
      advertising: false,
    };
    setCookieSettings(newSettings);
    updateConsent(newSettings);
    cookies.set("consent", true, {
      path: "/",
      expires: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000),
    });
    cookies.set("consent_settings", newSettings, {
      path: "/",
      expires: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000),
    });
    setIsVisible(false);
    setTimeout(() => setAccepted(true), 300);
  };

  const handleSaveSettings = () => {
    const newSettings = {
      essential: true,
      analytics: true,
      advertising: cookieSettings.advertising,
    };
    setCookieSettings(newSettings);
    updateConsent(newSettings);
    cookies.set("consent", true, {
      path: "/",
      expires: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000),
    });
    cookies.set("consent_settings", newSettings, {
      path: "/",
      expires: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000),
    });
    setShowSettings(false);
    setIsVisible(false);
    setTimeout(() => setAccepted(true), 300);
  };

  const handleToggleSetting = (key) => {
    setCookieSettings((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleBack = () => {
    setShowSettings(false); // Simply go back without saving
  };

  if (accepted) return null;

  return (
    <div
      className={`fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6 transition-all duration-300 ease-in-out transform ${
        isVisible ? "translate-y-0 opacity-100" : "translate-y-full opacity-0"
      }`}
    >
      <div className="container mx-auto max-w-4xl">
        <Card className="border shadow-lg">
          <CardHeader className="pb-2 flex flex-row items-start justify-between">
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              <p className="text-lg md:text-xl font-bold">Cookie Preferences</p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-full"
              onPress={handleAcceptEssential}
              aria-label="Close cookie banner"
            >
              <X className="h-4 w-4" />
            </Button>
          </CardHeader>
          <CardBody>
            <p className="text-sm md:text-base">
              We use essential cookies and analytics cookies to make our site
              work and improve your experience. You can also opt-in to
              advertising cookies for personalized content.
            </p>
          </CardBody>
          <CardFooter className="flex flex-col sm:flex-row gap-2 pt-0">
            {showSettings ? (
              <div className="w-full">
                <div className="space-y-4">
                  <div>
                    <label className="flex items-center gap-2">
                      <input type="checkbox" checked={true} disabled />
                      <span>Essential Cookies</span>
                    </label>
                    <p className="text-sm text-gray-500 ml-6">
                      Always enabled for basic functionality.
                    </p>
                  </div>
                  <div>
                    <label className="flex items-center gap-2">
                      <input type="checkbox" checked={true} disabled />
                      <span>Analytics Cookies</span>
                    </label>
                    <p className="text-sm text-gray-500 ml-6">
                      Always enabled to track site usage.
                    </p>
                  </div>
                  <div>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={cookieSettings.advertising}
                        onChange={() => handleToggleSetting("advertising")}
                      />
                      <span>Advertising Cookies</span>
                    </label>
                    <p className="text-sm text-gray-500 ml-6">
                      Enable personalized ads.
                    </p>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 mt-4">
                  <Button
                    variant="outline"
                    color="primary"
                    className="w-full sm:w-auto"
                    onPress={handleBack}
                  >
                    Back
                  </Button>
                  <Button
                    color="primary"
                    className="w-full sm:w-auto"
                    onPress={handleSaveSettings}
                  >
                    Save Settings
                  </Button>
                  <Button
                    className="w-full bg-1B51CA text-white sm:w-auto"
                    onPress={handleAcceptAll}
                  >
                    Accept All
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <Button
                  className="w-full sm:w-auto order-3 sm:order-1"
                  onPress={() => setShowSettings(true)}
                >
                  Advanced Settings
                </Button>
                <Button
                  color="primary"
                  className="w-full sm:w-auto order-2 sm:order-2"
                  onPress={handleAcceptEssential}
                >
                  Accept Essential
                </Button>
                <Button
                  className="w-full bg-1B51CA text-white sm:w-auto order-1 sm:order-3"
                  onPress={handleAcceptAll}
                >
                  Accept All
                </Button>
              </>
            )}
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}
